import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarDays, ChevronRight } from "lucide-react";
import { useVehicleEvents } from "@/hooks/useEvents";

interface VehicleEventsSectionProps {
  vehicleId: string;
  className?: string;
}

export function VehicleEventsSection({
  vehicleId,
  className = "",
}: VehicleEventsSectionProps) {
  const { t } = useTranslation(["pages", "common"]);
  const { data, isLoading, error } = useVehicleEvents(vehicleId, 3);
  const events = data?.events ?? [];

  return (
    <div
      className={`bg-white rounded-lg border border-secondary-200 ${className}`}
    >
      <div className="p-6 border-b border-secondary-100">
        <h2 className="text-lg font-semibold text-secondary-900 flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-primary-500" />
          {t("pages:vehicles.eventsSection.title")}
        </h2>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="animate-pulse space-y-2" data-testid="events-loading">
            <div className="h-10 bg-secondary-200 rounded"></div>
            <div className="h-10 bg-secondary-200 rounded"></div>
            <div className="h-10 bg-secondary-200 rounded"></div>
          </div>
        ) : error ? (
          <p className="text-sm text-red-600" role="alert">
            {t("pages:vehicles.eventsSection.noEvents")}
          </p>
        ) : events.length === 0 ? (
          <p className="text-sm text-secondary-500">
            {t("pages:vehicles.eventsSection.noEvents")}
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {events.map((event) => (
                <Link
                  key={event.id}
                  to={`/events/${event.id}`}
                  className="flex items-center justify-between p-3 bg-secondary-50 hover:bg-secondary-100 rounded-lg transition-colors"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-secondary-900 truncate">
                      {event.name}
                    </div>
                    <div className="text-xs text-secondary-500">
                      {new Date(event.startDate).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                      {" · "}
                      {event.track.name}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-secondary-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
            <div className="pt-4 mt-4 border-t border-secondary-100">
              <Link
                to={`/events?view=past&vehicleId=${vehicleId}`}
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                {t("pages:vehicles.eventsSection.seeMore")}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
